export const Token = {
  Text: 'Text',
  Wait: 'Wait',
  Loading: 'Loading',
  Glitch: 'Glitch',
  UnreadableGlitch: 'UnreadableGlitch',
  Typing: 'Typing',
  FlashText: 'FlashText',
  Cycle: 'Cycle',
} as const;

export type Token = (typeof Token)[keyof typeof Token];

export type RenderToken =
  | { kind: typeof Token.Text; text: string }
  | { kind: typeof Token.Wait; time: number }
  | { kind: typeof Token.Loading; time: number; startedAt: number | null }
  | {
      kind: typeof Token.Glitch;
      text: string;
      startedAt: number | null;
      seed: number;
      glitchIndices: number[];
      glitchEndAt: number[];
    }
  | { kind: typeof Token.UnreadableGlitch; length: number }
  | { kind: typeof Token.Typing; text: string; startedAt: number | null; playedCount: number }
  | { kind: typeof Token.FlashText; text: string }
  | { kind: typeof Token.Cycle; frames: string[] };

export const tokensThatEatTime: Token[] = [Token.Wait, Token.Loading, Token.Typing];

/**
 * parser for strings with inline codes like:
 *   %%WAIT(1000)%%, %%LOADING(2000)%%, %%GLITCH(hello)%%, etc.
 *
 * - Text outside of %%CODES%% is emitted as { kind: 'TEXT', text }
 * - Known codes are converted into typed tokens (see RenderToken)
 * - Unknown or malformed codes are preserved as literal TEXT
 */
export function parse(input: string) {
  const tokens: RenderToken[] = [];

  const pattern = /%%([A-Z_]+)(?:\(([\s\S]*?)\))?%%/g;
  let lastIndex = 0;

  const toNumber = (s: string): number | null => {
    const n = Number(s.trim());
    return Number.isFinite(n) ? n : null;
  };

  const literal = (s: string): RenderToken => ({ kind: Token.Text, text: s });

  const makeToken = (name: string, argRaw: string | undefined, fullMatch: string): RenderToken => {
    switch (name) {
      case 'WAIT': {
        const time = toNumber(argRaw ?? '');
        return time === null ? literal(fullMatch) : { kind: Token.Wait, time };
      }
      case 'LOADING': {
        const time = toNumber(argRaw ?? '');
        return time === null ? literal(fullMatch) : { kind: Token.Loading, time, startedAt: null };
      }
      case 'UNREADABLE_GLITCH': {
        const length = toNumber(argRaw ?? '');
        return length === null ? literal(fullMatch) : { kind: Token.UnreadableGlitch, length };
      }
      case 'GLITCH': {
        return typeof argRaw === 'string'
          ? {
              kind: Token.Glitch,
              text: argRaw,
              startedAt: null,
              seed: 0,
              glitchIndices: [],
              glitchEndAt: [],
            }
          : literal(fullMatch);
      }
      case 'TYPING': {
        return typeof argRaw === 'string'
          ? { kind: Token.Typing, text: argRaw, startedAt: null, playedCount: 0 }
          : literal(fullMatch);
      }
      case 'FLASHTEXT': {
        return typeof argRaw === 'string' ? { kind: Token.FlashText, text: argRaw } : literal(fullMatch);
      }
      case 'CYCLE': {
        if (typeof argRaw !== 'string') return literal(fullMatch);
        const frames = argRaw.split(',');
        return frames.length > 0 ? { kind: Token.Cycle, frames } : literal(fullMatch);
      }
      default:
        // preserve unknowns as literal text
        return literal(fullMatch);
    }
  };

  for (const match of input.matchAll(pattern)) {
    const fullMatch = match[0];
    const name = match[1];
    const arg = match[2];

    // output any preceding plain text
    if (match.index !== undefined && match.index > lastIndex) {
      const plain = input.slice(lastIndex, match.index);
      if (plain.length > 0) tokens.push({ kind: Token.Text, text: plain });
    }

    // output parsed token (or literal if malformed/unknown)
    tokens.push(makeToken(name ?? '', arg, fullMatch));

    // advance lastIndex to end of this match
    const end = (match.index ?? 0) + fullMatch.length;
    lastIndex = end;
  }

  // output any remaining trailing text
  if (lastIndex < input.length) {
    tokens.push({ kind: Token.Text, text: input.slice(lastIndex) });
  }

  return tokens;
}
